import { Link } from "react-router-dom";
import {
  ArrowRight,
  Crown,
  Calendar,
  Gift,
  Percent,
  Star,
  ShieldCheck,
} from "@phosphor-icons/react";

const VIPMembership = () => {
  const membershipBenefits = [
    {
      icon: <Calendar size={24} weight="light" />,
      title: "Priority Scheduling",
      description: "Book appointments first, with extended consultation times and flexible hours.",
    },
    {
      icon: <Percent size={24} weight="light" />,
      title: "Exclusive Pricing",
      description: "Enjoy 15% savings on all treatments and 20% off skincare products.",
    },
    {
      icon: <Gift size={24} weight="light" />,
      title: "Birthday Perks",
      description: "Celebrate with a complimentary treatment of your choice during your birthday month.",
    },
    {
      icon: <Star size={24} weight="light" />,
      title: "Member-Only Treatments",
      description: "Access to exclusive procedures and new treatments before public launch.",
    },
    {
      icon: <ShieldCheck size={24} weight="light" />,
      title: "Concierge Service",
      description: "Direct access to your aesthetic team via private messaging and priority callbacks.",
    },
    {
      icon: <Crown size={24} weight="light" />,
      title: "VIP Events",
      description: "Invitations to exclusive events, masterclasses, and product launches.",
    },
  ];

  const tiers = [
    {
      name: "Gold",
      price: "$199",
      period: "per month",
      description: "Perfect for those beginning their aesthetic journey",
      benefits: [
        "10% off all treatments",
        "15% off skincare products",
        "Priority scheduling",
        "Quarterly skin consultations",
        "Birthday complimentary service",
      ],
      featured: false,
    },
    {
      name: "Platinum",
      price: "$399",
      period: "per month",
      description: "Our most popular tier for dedicated clients",
      benefits: [
        "15% off all treatments",
        "20% off skincare products",
        "Priority scheduling + extended appointments",
        "Monthly skin consultations",
        "Birthday complimentary service",
        "Access to member-only treatments",
        "1 complimentary HydraFacial per quarter",
      ],
      featured: true,
    },
    {
      name: "Black",
      price: "$799",
      period: "per month",
      description: "The ultimate in personalized aesthetic care",
      benefits: [
        "20% off all treatments",
        "25% off skincare products",
        "Concierge scheduling & direct provider access",
        "Unlimited consultations",
        "Birthday treatment of choice",
        "First access to all new treatments",
        "2 complimentary treatments per month",
        "Annual skincare protocol review",
        "VIP event invitations",
      ],
      featured: false,
    },
  ];

  return (
    <div data-testid="vip-page" className="pt-20">
      {/* Hero Section - Dark Theme */}
      <section className="vip-section py-24 md:py-32 lg:py-40 relative">
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <Crown size={24} weight="light" className="text-taupe" />
              <p className="text-xs uppercase tracking-[0.3em] text-taupe">
                Exclusive Access
              </p>
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.9] text-smoke">
              VIP
              <br />
              <span className="italic">Membership</span>
            </h1>
            <p className="mt-8 text-base md:text-lg font-light text-smoke/60 leading-relaxed max-w-xl">
              Experience the pinnacle of personalized aesthetic care. Our VIP members 
              enjoy priority access, exclusive treatments, and bespoke protocols 
              designed to elevate your beauty journey.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-24 md:py-32 bg-smoke">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-16 md:mb-24">
            <p className="text-xs uppercase tracking-[0.3em] text-taupe mb-4">
              Member Benefits
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-matte">
              The VIP Experience
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {membershipBenefits.map((benefit, index) => (
              <div
                key={benefit.title}
                data-testid={`benefit-${index}`}
                className="p-8 border border-matte/5 hover:border-taupe/30 transition-all duration-300"
              >
                <div className="text-taupe mb-6">{benefit.icon}</div>
                <h3 className="font-serif text-xl text-matte mb-3">
                  {benefit.title}
                </h3>
                <p className="text-sm font-light text-matte/60 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Membership Tiers */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-16 md:mb-24">
            <p className="text-xs uppercase tracking-[0.3em] text-taupe mb-4">
              Membership Tiers
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-matte">
              Choose Your Level
            </h2>
            <p className="mt-6 text-base font-light text-matte/60 max-w-lg mx-auto">
              Select the membership tier that best fits your aesthetic goals and lifestyle.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {tiers.map((tier, index) => (
              <div
                key={tier.name}
                data-testid={`tier-${index}`}
                className={`relative p-8 md:p-10 transition-all duration-300 ${
                  tier.featured
                    ? "bg-matte text-smoke border border-matte"
                    : "bg-white border border-matte/10 hover:border-taupe/30"
                }`}
              >
                {tier.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-taupe text-matte text-xs uppercase tracking-wider px-4 py-1">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3
                    className={`font-serif text-2xl mb-2 ${
                      tier.featured ? "text-smoke" : "text-matte"
                    }`}
                  >
                    {tier.name}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span
                      className={`font-serif text-4xl ${
                        tier.featured ? "text-smoke" : "text-matte"
                      }`}
                    >
                      {tier.price}
                    </span>
                    <span
                      className={`text-sm ${
                        tier.featured ? "text-smoke/60" : "text-matte/50"
                      }`}
                    >
                      {tier.period}
                    </span>
                  </div>
                  <p
                    className={`text-sm mt-3 ${
                      tier.featured ? "text-smoke/60" : "text-matte/50"
                    }`}
                  >
                    {tier.description}
                  </p>
                </div>

                <ul className="space-y-4 mb-10">
                  {tier.benefits.map((benefit, benefitIndex) => (
                    <li
                      key={benefitIndex}
                      className={`flex items-start gap-3 text-sm ${
                        tier.featured ? "text-smoke/80" : "text-matte/70"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${
                          tier.featured ? "bg-taupe" : "bg-taupe"
                        }`}
                      />
                      {benefit}
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  data-testid={`tier-cta-${index}`}
                  className={`w-full inline-flex items-center justify-center gap-2 py-3 text-xs uppercase tracking-[0.15em] transition-all duration-300 ${
                    tier.featured
                      ? "bg-transparent border border-taupe text-smoke hover:bg-taupe hover:text-matte"
                      : "bg-transparent border border-taupe text-matte hover:bg-taupe hover:text-white"
                  }`}
                >
                  Join {tier.name}
                  <ArrowRight size={14} weight="light" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 md:py-32 bg-smoke">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-matte">
                Common Questions
              </h2>
            </div>

            <div className="space-y-6">
              {[
                {
                  q: "Can I upgrade or downgrade my membership?",
                  a: "Yes, you can change your membership tier at any time. Changes take effect at your next billing cycle.",
                },
                {
                  q: "Is there a commitment period?",
                  a: "Our memberships are month-to-month with no long-term commitment required. Cancel anytime.",
                },
                {
                  q: "Can I use my benefits on friends or family?",
                  a: "Membership benefits are non-transferable, but we offer special referral bonuses when your friends join.",
                },
                {
                  q: "When do my monthly credits expire?",
                  a: "Complimentary treatments can be used anytime during your membership. Unused credits do not roll over.",
                },
              ].map((faq, index) => (
                <div
                  key={index}
                  data-testid={`faq-${index}`}
                  className="border-b border-matte/10 pb-6"
                >
                  <h4 className="font-serif text-lg text-matte mb-2">{faq.q}</h4>
                  <p className="text-sm font-light text-matte/60">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="vip-section py-24 md:py-32 relative">
        <div className="container mx-auto px-4 md:px-8 relative z-10 text-center">
          <Crown size={40} weight="light" className="text-taupe mx-auto mb-6" />
          <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight text-smoke">
            Elevate Your Experience
          </h2>
          <p className="mt-6 text-base font-light text-smoke/60 max-w-lg mx-auto">
            Join our VIP community and discover a new level of personalized aesthetic care.
          </p>
          <Link
            to="/contact"
            data-testid="vip-cta-btn"
            className="mt-10 inline-flex items-center gap-3 border border-taupe text-smoke bg-transparent hover:bg-taupe hover:text-matte uppercase tracking-[0.2em] text-xs font-medium px-8 py-3 transition-all duration-300"
          >
            Become a VIP Member
            <ArrowRight size={16} weight="light" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default VIPMembership;
